import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';
import { DEFAULT_ROWS_PER_PAGE } from '../../utils/paginationUtils';

export const fetchReservations = createAsyncThunk('reservations/fetch', (args = {}, { getState }) => {
  const state = getState();
  const { eventId } = args;

  if (eventId) {
    const { search, page, limit } = {
      search: args.search ?? state.reservations.options.search ?? 0,
      page: args.page ?? state.reservations.options.page ?? 1,
      limit: args.limit ?? state.reservations.options.limit ?? 10,
    };
    return api
      .get(
        `/events/${eventId}/reservations?page=${encodeURIComponent(page)}&limit=${encodeURIComponent(
          limit
        )}&search=${encodeURIComponent(search)}`
      )
      .then((response) => response.data);
  } else {
    const { search, from, to, status, page, limit } = state.reservations.options;
    return api
      .get(
        `/reservations?to=${encodeURIComponent(to)}&from=${encodeURIComponent(from)}&status=${encodeURIComponent(
          status
        )}&search=${encodeURIComponent(search)}&page=${encodeURIComponent(page)}&limit=${encodeURIComponent(limit)}`
      )
      .then((response) => response.data);
  }
});

export const createReservation = createAsyncThunk('reservations/create', (args, { dispatch, rejectWithValue }) => {
  const { session, event_id, user, object_ids, expiration, notes } = args;
  return api
    .post(`/events/${event_id}/reservations`, {
      user,
      object_ids,
      expiration,
      notes,
      session,
    })
    .then((response) => {
      dispatch(fetchReservations({ eventId: event_id }));
      return { status: response.status, data: response.data };
    })
    .catch((err) => {
      if (err.response?.data?.errorType === 'RESERVATION_INVENTORY_UNCERTAIN') {
        dispatch(fetchReservations(event_id ? { eventId: event_id } : {}));
      }
      return rejectWithValue({ status: err.response?.status, data: err.response?.data });
    });
});

export const completeReservation = createAsyncThunk(
  'reservations/complete',
  (args, { dispatch, rejectWithValue, getState }) => {
    const { reservation_id, payment_type, receipt_nr, user, object_ids, event_id, language } = args;
    return api
      .post(`/reservations/${reservation_id}/orders`, { payment_type, receipt_nr, user, object_ids, language })
      .then((response) => {
        dispatch(fetchReservations(event_id ? { eventId: event_id } : {}));
        return { status: response.status, data: response.data };
      })
      .catch((err) => rejectWithValue({ status: err.response?.status, data: err.response?.data }));
  }
);

export const patchReservation = createAsyncThunk('reservations/patch', (args, { dispatch, rejectWithValue }) => {
  const { reservation_id, status, event_id } = args;
  return api
    .patch(`/reservations/${reservation_id}`, { status })
    .then((response) => {
      dispatch(fetchReservations(event_id ? { eventId: event_id } : {}));
      return { status: response.status, data: response.data };
    })
    .catch((err) => {
      if (err.response?.data?.errorType === 'RESERVATION_INVENTORY_UNCERTAIN') {
        dispatch(fetchReservations(event_id ? { eventId: event_id } : {}));
      }
      return rejectWithValue({ status: err.response?.status, data: err.response?.data });
    });
});

export const putReservation = createAsyncThunk('reservations/put', (args, { dispatch, rejectWithValue, getState }) => {
  const { reservation_id, user, expiration, notes, object_ids, event_id, session } = args;
  return api
    .put(`/reservations/${reservation_id}`, { user, expiration, notes, object_ids, session })
    .then((response) => {
      dispatch(fetchReservations(event_id ? { eventId: event_id } : {}));
      return { status: response.status, data: response.data };
    })
    .catch((err) => {
      if (err.response?.data?.errorType === 'RESERVATION_INVENTORY_UNCERTAIN') {
        dispatch(fetchReservations(event_id ? { eventId: event_id } : {}));
      }
      return rejectWithValue({ status: err.response?.status, data: err.response?.data });
    });
});

const initialState = {
  data: [],
  totalItems: 0,
  error: null,
  isLoading: false,

  options: {
    search: '',
    from: null,
    to: null,
    status: 'ALL',
    page: 1,
    limit: DEFAULT_ROWS_PER_PAGE,
  },
};

const reservationsSlice = createSlice({
  name: 'reservations',
  initialState: initialState,
  reducers: {
    setFilters: (state, action) => {
      const { search, from, to, status, page, limit } = action.payload;

      if (search !== undefined) state.options.search = search;
      if (from !== undefined) state.options.from = from;
      if (to !== undefined) state.options.to = to;
      if (status !== undefined) state.options.status = status;
      if (page !== undefined) state.options.page = page;
      if (limit !== undefined) state.options.limit = limit;
    },
    clearReservations: (state) => {
      state.data = [];
      state.options = {
        search: '',
        from: null,
        to: null,
        status: 'ALL',
        page: 1,
        limit: DEFAULT_ROWS_PER_PAGE,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchReservations.pending, (state, action) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(fetchReservations.fulfilled, (state, action) => {
      state.isLoading = false;
      state.data = action.payload.data;
      state.totalItems = action.payload.totalItems;
      state.options.page = action.payload.currentPage;
    });

    builder.addCase(fetchReservations.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message;
    });

    builder.addCase(createReservation.pending, (state, action) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(createReservation.fulfilled, (state, action) => {
      state.isLoading = false;
    });

    builder.addCase(createReservation.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message;
    });

    builder.addCase(completeReservation.pending, (state, action) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(completeReservation.fulfilled, (state, action) => {
      state.isLoading = false;
    });

    builder.addCase(completeReservation.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message;
    });

    builder.addCase(patchReservation.pending, (state, action) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(patchReservation.fulfilled, (state, action) => {
      state.isLoading = false;
    });

    builder.addCase(patchReservation.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message;
    });

    builder.addCase(putReservation.pending, (state, action) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(putReservation.fulfilled, (state, action) => {
      state.isLoading = false;
    });

    builder.addCase(putReservation.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message;
    });
  },
});

export const { setFilters, clearReservations } = reservationsSlice.actions;
export default reservationsSlice.reducer;
